/* ══════════════════════════════════════════════════════════════════════════
 * MOVING BETWEEN SCREENS WITHOUT RELOADING THE APP — 1 October 2026.
 *
 * Al, 1 October 2026: *"the entire app is so sluggish. You click on, for
 * example, location and it takes forever. Nothing happens… I have to do it
 * again and again."*
 *
 * MEASURED, not guessed: every request from his Mac crossed the world (it
 * leaves through Toronto and the server is in Singapore) for about 1.4s before
 * Eleat did any work — and every tap in this app was a FULL PAGE LOAD: the
 * page, then the code checked again, then the data. Four seconds or more with
 * nothing on screen to say a tap had landed, so he tapped again.
 *
 * Every screen is still chosen by the address (`main.jsx` reads `?venue=…`,
 * `?explore=1`, …) exactly as before — no router library, no new dependency.
 * What changes is HOW the address changes: a link to another Eleat screen
 * now updates the address in place and the app redraws that screen. The page,
 * the code and the splash are not fetched again; only the screen's own data
 * travels. The back button works the same (`popstate`).
 *
 * ⚠️ EACH SCREEN STILL STARTS FRESH. `main.jsx` gives the screen a `key` made
 * of the address, so React builds it new every time, exactly as a page load
 * did — nothing a screen held can leak into the next one.
 *
 * ⚠️ WHAT STILL RELOADS, ON PURPOSE: signing in, signing out, joining, the
 * error screen and the new-version check. Those change who is signed in or
 * what code is running, and a clean start is the safe answer there.
 * ══════════════════════════════════════════════════════════════════════════ */

export const NAV_EVENT = 'eleat:navigate';

/* An address this app draws itself: the same site, the root page, and only a
   query string (`?venue=…`) — never another host, never another path such as
   the legal pages `eleat-api` serves. */
function inApp(href) {
  try {
    const u = new URL(href, window.location.href);
    return u.origin === window.location.origin && (u.pathname === '/' || u.pathname === '');
  } catch { return false; }
}

/* Go to a screen. Falls back to an ordinary load for anything that is not
   one of this app's own screens, so a wrong call can only be slower, never
   stuck. */
export function go(href, { replace = false } = {}) {
  if (!inApp(href)) { window.location.href = href; return; }
  const u = new URL(href, window.location.href);
  const next = u.search + u.hash;
  const now = window.location.search + window.location.hash;
  if (next === now && !replace) { window.scrollTo(0, 0); return; }
  /* `eleat: 1` marks an entry this app pushed, so a Back control knows there
     is an Eleat screen behind it (Al, 7 Oct 2026: back from a restaurant went
     Home whatever screen it was opened from). */
  if (replace) window.history.replaceState(window.history.state, '', u.pathname + next);
  else window.history.pushState({ eleat: 1 }, '', u.pathname + next);
  window.scrollTo(0, 0);
  window.dispatchEvent(new Event(NAV_EVENT));
}

/* Every <a href="?…"> in the app, caught once here rather than in 76 places.
   Left alone: a new tab or window (modifier keys, middle click, target),
   downloads, another host, and anything a screen already handled itself. */
export function catchLinks() {
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0) return;
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    if (a.target && a.target !== '_self') return;
    if (a.hasAttribute('download')) return;
    const href = a.getAttribute('href');
    if (!href || href.startsWith('#') || /^(mailto|tel|sms|whatsapp):/i.test(href)) return;
    if (!inApp(a.href)) return;
    e.preventDefault();
    go(a.href);
  });
}

/* Back to the Eleat screen this one was opened from; Home when there is none
   (a shared link, a new tab). */
export function goBack(fallback = '?') {
  if (window.history.state && window.history.state.eleat) window.history.back();
  else go(fallback);
}
